import { forwardRef } from 'react';
import cx from 'classnames';
import { isCustomSection, MenuListItem, MenuListSection } from '.';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { BusyStateIndicator } from '../../BusyStateIndicator';
import { useDensity } from '../../Density';
import { getListTokens } from '../../ListBox/__internal__/utils';
import cn from './MenuListDesign.css.json';
import './MenuListDesign.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MenuListDesign = /*#__PURE__*/forwardRef(function MenuListDesign(props, ref) {
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const tokens = getMenuDesignTokens(dt, {
    tokenOverrides: props.tokens
  });
  const hasMenuItemIcon = props.sections.some(section => !isCustomSection(section) && section.items.some(item => item.icon));
  const disabled = props.disabled || !!props.sections.length && !props.sections.some(section => isCustomSection(section) || section.items.some(item => !item.disabled));
  const renderCustomSection = (section, sectionIndex) => {
    return section.render({
      index: sectionIndex,
      key: sectionIndex,
      focusLocation: props.focusLocation?.[0]?.[0] === sectionIndex ? props.focusLocation : null,
      onItemHovered: props.onItemHovered,
      onItemLeaved: props.onItemLeaved
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    onKeyDown: props.onKeyDown,
    onKeyUp: props.onKeyUp,
    className: cx(cn.listContainer, props.className, props.isBusy && cn.isBusy),
    style: {
      ...tokens,
      ...props.style,
      ...props.theme?.(dt)
    },
    children: [props.sections.map((section, sectionIndex) => {
      if (!isCustomSection(section) || !section.showAboveMenu) return null;
      return renderCustomSection(section, sectionIndex);
    }), /*#__PURE__*/_jsx("div", {
      ref: ref,
      tabIndex: props.tabIndex ?? 0,
      role: !props.isBusy ? 'menu' : undefined,
      "aria-disabled": disabled,
      children: props.isBusy ? /*#__PURE__*/_jsx(BusyStateIndicator, {
        active: true,
        delay: 0
      }) : /*#__PURE__*/_jsx("ul", {
        className: cx(cn.list),
        role: "presentation",
        style: {
          ...getListTokens(dt, density),
          ...props.listTheme?.(dt)
        },
        children: props.sections.map((section, sectionIndex) => {
          if (isCustomSection(section)) {
            return section.showAboveMenu ? null : renderCustomSection(section, sectionIndex);
          }
          const hasMenuItemRightIcon = section.items.some(item => item.closable || item.subSections);
          const allSectionItemsDisabled = !section.items.some(item => !item.disabled);
          return /*#__PURE__*/_jsx(MenuListSection, {
            section: section,
            index: sectionIndex,
            disabled: props.disabled || allSectionItemsDisabled,
            onSubmenuClosed: () => {
              props.onSubmenuClosed?.(props.menuLevel);
            },
            children: section.items.map((item, itemIndex) => {
              if (section.selectedKey !== undefined) item.checked = section.selectedKey === item.key;else if (section.selectedIndex !== undefined) item.checked = section.selectedIndex === itemIndex;
              return /*#__PURE__*/_jsx(MenuListItem, {
                ref: sectionIndex === 0 && itemIndex === 0 ? props.firstItemRef : undefined,
                item: item,
                disabled: props.disabled || allSectionItemsDisabled || !item.subSections?.length && item.disabled,
                closable: item.closable,
                index: itemIndex,
                listIndex: getListIndex([sectionIndex, itemIndex], props.sections),
                sectionIndex: sectionIndex,
                additionalAriaText: item.additionalAriaText,
                onSelected: props.onItemSelected,
                onClosed: props.onItemClosed,
                showFocus: props.showFocus,
                focusLocation: props.focusLocation,
                onItemHovered: props.onItemHovered,
                onItemLeaved: props.onItemLeaved,
                onSubmenuClosed: props.onSubmenuClosed,
                testId: item.testId,
                isRadio: section.selectedIndex !== undefined,
                menuHasIndentation: hasIndentation(props.sections),
                tokens: props.tokens,
                menuLevel: props.menuLevel,
                needStartIconSpacing: hasMenuItemIcon,
                hasMenuItemRightIcon: hasMenuItemRightIcon,
                onEscape: props.onKeyDown,
                onSpace: props.onKeyDown,
                popoverTheme: props.popoverTheme,
                listTheme: props.listTheme,
                menuTheme: props.theme
              }, `${itemIndex}-${item.text}`);
            })
          }, sectionIndex);
        })
      })
    })]
  });
});
export const getMenuDesignTokens = (dt, opts) => {
  return {
    ...renderDesignTokens({
      'disabled-opacity': 1,
      'disabled-color': dt.tokens['color-action-text-standard-disabled'],
      'font-weight-thick': dt.tokens['font-style-weight-700'],
      'heading-size-small': dt.tokens['font-size-body-text-s'],
      'font-family': opts?.tokenOverrides?.().fontFamily ?? 'inherit',
      'title-fg': dt.tokens['color-action-text-standard-normal']
    }, 'menu'),
    ...renderDesignTokens({
      'stretch-xs': `${dt.tokens['space-base-xs']} ${dt.tokens['space-base-xxs']}`,
      'squish-s': `${dt.tokens['space-base-xs']} ${dt.tokens['space-base-s']}`,
      l: dt.tokens['space-base-l'],
      m: dt.tokens['space-base-m'],
      s: dt.tokens['space-base-s'],
      xs: dt.tokens['space-base-xs']
    }, 'menu-spacing'),
    ...renderDesignTokens({
      'spacing-m': dt.tokens['space-base-m'],
      'spacing-s': dt.tokens['space-base-s'],
      'spacing-xs': dt.tokens['space-base-xs'],
      'spacing-xxs': dt.tokens['space-base-xxs'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      'font-size-s': dt.tokens['font-size-body-text-s'],
      'font-size-xs': dt.tokens['font-size-body-text-xs'],
      'description-fg': dt.tokens['color-label-text'],
      fg: undefined,
      'font-family': 'inherit'
    }, 'menu-list-item'),
    ...renderDesignTokens({
      'hover-fg': dt.tokens['color-action-text-standard-hover'],
      'hover-bg': dt.tokens['color-action-background-standard-hover'],
      'active-fg': dt.tokens['color-action-text-standard-selected'],
      'active-bg': dt.tokens['color-action-background-standard-selected'],
      'active-filter': dt.stateStandardNova.active.filter
    }, 'close-button-icon'),
    ...(!dt.enableNovaTokens && getMenuLegacyDesignTokens(dt, opts))
  };
};
export const getMenuLegacyDesignTokens = (dt, opts) => {
  let titleFg = '';
  let descriptionFg = '';
  switch (dt.themeVariant) {
    case 'dark':
      titleFg = dt.c.white;
      descriptionFg = dt.c.white80;
      break;
    case 'high-contrast':
      titleFg = dt.c.white;
      descriptionFg = dt.c.white;
      break;
    default:
      titleFg = dt.c.n_3;
      descriptionFg = dt.c.n_1;
  }
  return {
    ...renderDesignTokens({
      'disabled-opacity': 0.3,
      'disabled-color': dt.reportTokens.stateStandard.disabled.fg,
      'font-weight-thick': dt.reportTokens.font.weight.thick,
      'heading-size-small': dt.tokens['font-size-body-text-s'],
      'font-family': opts?.tokenOverrides?.().fontFamily ?? 'inherit',
      'title-fg': titleFg
    }, 'menu'),
    ...renderDesignTokens({
      'font-size-m': dt.reportTokens.font.size.m,
      'font-size-s': dt.reportTokens.font.size.s,
      'font-size-xs': dt.reportTokens.font.size.xs,
      'description-fg': descriptionFg
    }, 'menu-list-item'),
    ...renderDesignTokens({
      'hover-fg': dt.reportTokens.stateStandard.hover.fg,
      'hover-bg': dt.reportTokens.stateStandard.hover.bg,
      'active-fg': dt.reportTokens.stateStandard.active.fg,
      'active-bg': dt.reportTokens.stateStandard.active.bg,
      'active-filter': dt.reportTokens.stateStandard.active.filter
    }, 'close-button-icon')
  };
};
function getListIndex(location, sections) {
  const [sectionIndex, itemIndex] = location;
  const itemCounts = sections.map(section => isCustomSection(section) ? 0 : section.items.length);
  const itemsInPreviousSections = itemCounts.slice(0, sectionIndex).reduce((a, b) => a + b, 0);
  return itemsInPreviousSections + itemIndex;
}

// Determine if any item has an indentation greater than 0
export function hasIndentation(sections) {
  function _checkItems(items) {
    // make sure `items` is iterable
    // https://stackoverflow.com/a/53106917/2295034
    if (Symbol.iterator in Object(items) === false) return false;
    for (const {
      indentation,
      subSections
    } of items) {
      if (indentation > 0) return true;
      if (subSections) return hasIndentation(subSections);
    }
    return false;
  }
  return sections.some(section => _checkItems(section.items));
}
//# sourceMappingURL=MenuListDesign.js.map